import { useEffect, useRef, useState } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { useAppServices } from './ViewContext';

/**
 * Export gouverné depuis une liste (ADR-072 §4, ADR-094) : `RunExport` sur le jeu déclaré par la
 * vue, jamais la route de liste avec une grande limite. Les paramètres du jeu sont pré-remplis
 * depuis l'état de la liste (`parameter_map`) ; un filtre de la liste sans paramètre
 * correspondant ne s'applique pas, et la boîte le dit. Le serveur réautorise la permission
 * `export`, applique les états de champ et le plafond de classification ; un refus s'affiche tel
 * quel. Le fichier se télécharge par l'URL signée de `GetFileDownload`.
 */

export type ListExport = NonNullable<ViewResolution['list_export']>;

type Phase = { step: 'form' } | { step: 'running' } | { step: 'ready'; file_name: string; url: string; rows?: number; excluded: string[] } | { step: 'failed'; message: string; denied: boolean };

const POLL_MS = 1000;
const POLL_TRIES = 60;

export function ListExportDialog({ exported, initial, unmapped, onClose }: { exported: ListExport; initial: Readonly<Record<string, string>>; unmapped: readonly string[]; onClose: () => void }) {
  const services = useAppServices();
  const [values, setValues] = useState<Record<string, string>>({ ...initial });
  const [format, setFormat] = useState<string>(exported.formats[0] ?? 'csv');
  const [phase, setPhase] = useState<Phase>({ step: 'form' });
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const missing = exported.parameters.filter((parameter) => parameter.required === true && (values[parameter.name] ?? '') === '');

  const run = async (): Promise<void> => {
    setPhase({ step: 'running' });
    try {
      const parameters: Record<string, string> = {};
      for (const parameter of exported.parameters) if ((values[parameter.name] ?? '') !== '') parameters[parameter.name] = values[parameter.name]!;
      const response = await services.client.command<{ result?: { export_run_id?: string } }>('RunExport', { payload: { dataset_id: exported.dataset_id, format, parameters } });
      const runId = response.result?.export_run_id;
      if (runId === undefined) throw new Error('Export lancé sans identifiant de suivi.');
      for (let attempt = 0; attempt < POLL_TRIES && alive.current; attempt += 1) {
        const run = (await services.client.getObject<Record<string, unknown>>('ExportRun', runId)).data;
        if (run['status'] === 'completed' && typeof run['file_id'] === 'string') {
          const file = (await services.client.query<{ file_name: string; download: { url: string } }>('GetFileDownload', { file_id: run['file_id'] })).data;
          if (alive.current) setPhase({ step: 'ready', file_name: file.file_name, url: file.download.url, ...(typeof run['row_count'] === 'number' ? { rows: run['row_count'] } : {}), excluded: Array.isArray(run['excluded_fields']) ? (run['excluded_fields'] as string[]) : [] });
          return;
        }
        if (run['status'] === 'failed') throw new Error(`L’export a échoué${typeof run['error_code'] === 'string' ? ` (${run['error_code']})` : ''}.`);
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
      if (alive.current) setPhase({ step: 'failed', message: 'L’export est toujours en cours : il sera disponible plus tard.', denied: false });
    } catch (caught) {
      if (!alive.current) return;
      const denied = caught instanceof ApiError && (caught.category === 'AUTHORIZATION_ERROR' || caught.category === 'POLICY_REJECTION');
      setPhase({ step: 'failed', message: caught instanceof Error ? caught.message : String(caught), denied });
    }
  };

  return (
    <Dialog title={`Exporter — ${text(exported.label)}`} open onClose={onClose} size="md">
      <form
        className="ay-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (missing.length === 0 && phase.step !== 'running') void run();
        }}
      >
        {phase.step === 'failed' && (
          <p className={`ay-banner ${phase.denied ? 'ay-banner--warning' : 'ay-banner--danger'}`} role="alert">
            {phase.denied ? `Export refusé : ${phase.message}` : phase.message}
          </p>
        )}
        {phase.step === 'ready' ? (
          <div className="ay-export__ready" role="status">
            <p>
              Fichier prêt{phase.rows === undefined ? '' : ` : ${phase.rows.toLocaleString('fr')} ligne${phase.rows > 1 ? 's' : ''}`}.
            </p>
            {phase.excluded.length > 0 && <p className="ay-export__note">Colonnes écartées par la sécurité ou la classification : {phase.excluded.join(', ')}.</p>}
            <a className="ay-button ay-button--primary" href={phase.url} download={phase.file_name} rel="noopener">
              Télécharger {phase.file_name}
            </a>
          </div>
        ) : (
          <>
            <fieldset className="ay-field">
              <legend className="ay-field__label">Format</legend>
              <div className="ay-export__formats">
                {exported.formats.map((entry) => (
                  <label key={entry} className="ay-check">
                    <input type="radio" name="export-format" value={entry} checked={format === entry} onChange={() => setFormat(entry)} />
                    {entry.toUpperCase()}
                  </label>
                ))}
              </div>
            </fieldset>
            {exported.parameters.map((parameter) => (
              <label key={parameter.name} className="ay-field">
                <span className="ay-field__label">
                  {PARAMETER_LABELS[parameter.name] ?? parameter.name}
                  {parameter.required === true ? ' *' : ''}
                </span>
                <input
                  type={parameter.type === 'date' ? 'date' : parameter.type === 'number' ? 'number' : 'text'}
                  value={values[parameter.name] ?? ''}
                  required={parameter.required === true}
                  onChange={(event) => setValues((current) => ({ ...current, [parameter.name]: event.target.value }))}
                />
              </label>
            ))}
            {unmapped.length > 0 && <p className="ay-export__note">Non appliqués à l’export (aucun paramètre du jeu) : {unmapped.join(', ')}.</p>}
            <p className="ay-export__note">Les colonnes et le plafond de classification sont ceux du jeu déclaré ; les champs que vous ne pouvez pas lire en sont retirés.</p>
          </>
        )}
        <DialogActions
          cancel={{ label: phase.step === 'ready' ? 'Fermer' : 'Annuler', onClick: onClose }}
          primary={phase.step === 'ready' ? undefined : { label: phase.step === 'running' ? 'Export en cours…' : 'Lancer l’export', type: 'submit', disabled: missing.length > 0, busy: phase.step === 'running' }}
        />
      </form>
    </Dialog>
  );
}

const PARAMETER_LABELS: Readonly<Record<string, string>> = { from: 'Du', to: 'Au (exclu)' };
